import { Injectable } from '@angular/core';

import { hashCodeFromString } from '@blaze-shared/shared-util';

interface StylesCleanupEntry {
  count: number;
  timeout?: ReturnType<typeof setTimeout>;
  content?: number;
}

/**
 * {@link StylesCleanup} keeps cache of style cleanup tasks
 * from destroyed elements. if the element appears again before
 * its timeout (ex. during drag-n-drop) the style won't be removed
 * from the DOM.
 */
@Injectable({ providedIn: 'root' })
export class StylesCleanup {
  private cache: Record<string, StylesCleanupEntry> = {};

  register(keys: string | string[]): void {
    if (!Array.isArray(keys)) {
      this.getEntry(keys).count++;
      return;
    }
    keys.forEach((key) => {
      this.getEntry(key).count++;
    });
  }

  update(key: string, content: string): void {
    const entry = this.getEntry(key);
    if (entry.timeout !== undefined) {
      clearTimeout(entry.timeout);
      entry.timeout = undefined;
    }
    // NOTE to spare memory only a hashed version is cached for comparison purposes
    // this is to prevent DOM updates when there are no content changes
    const hashed = hashCodeFromString(content);
    if (entry.content === hashed) {
      return;
    }

    entry.content = hashed;
    const { head } = document;
    const existing = head.querySelector(`style#${key}`);
    const style = existing || newStyleTag(key);
    style.innerHTML = content;
    if (!existing) {
      head.appendChild(style);
    }
  }

  dispose(keys: string | string[]): void {
    if (!Array.isArray(keys)) {
      keys = [keys];
    }

    const toRemove = keys
      .map((key) => {
        // NOTE this mechanism is to prevent unnecessary DOM manipulations ex. while dragging a component over the canvas
        const entry = this.cache[key];
        if (entry) {
          entry.count--;
        }
        return { key, entry };
      })
      .filter(({ entry }) => entry?.count === 0);

    if (!toRemove.length) {
      return;
    }
    const timeout = setTimeout(() => {
      toRemove.forEach(({ key }) => {
        delete this.cache[key];
        document.head.querySelector(`style#${key}`)?.remove();
      });
    }, 500);
    toRemove.forEach(({ entry }) => (entry.timeout = timeout));
  }

  private getEntry(key: string): StylesCleanupEntry {
    return this.cache[key] || (this.cache[key] = { count: 0 });
  }
}

function newStyleTag(id: string): HTMLStyleElement {
  const style = document.createElement('style');
  style.id = id;
  return style;
}
